import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { PAGE_GUTTER_DESKTOP } from '../../../theaWeb/lib/pageGutter';

// Auto-advance dwell — how long each card holds in the center before the
// carousel scrolls to the next one. User-initiated scroll resets the timer
// for the newly-centered card.
const DWELL_MS = 5000;

export interface Testimonial {
  quote: string;
  attribution: string;
}

export const DEFAULT_TESTIMONIALS: Testimonial[] = [
  {
    quote:
      'My grandson called me today with the biggest smile on his face. I updated his interests to taekwondo and puzzles, and the ideas were so spot on. He thinks I’m the coolest grandma.',
    attribution: 'Susan',
  },
  {
    quote:
      'Thea suggested an at-home pottery date night kit for my wife. We haven’t laughed that hard together in months. Already planning the next one.',
    attribution: 'Ben R.',
  },
  {
    quote:
      'I give way more "just because" gifts now. Sent my best friend a dried flower bouquet last week and she called me crying. We’d been out of touch for a while.',
    attribution: 'Anika S.',
  },
  {
    quote:
      'I started a board just for myself — a little treat-yourself wishlist. Honestly, it’s made me better at knowing what I actually want, which helps everyone around me too.',
    attribution: 'Priya D.',
  },
];

// `$compact`: THE-1161 extends the existing THE-1081 `compact` prop (it is
// already hubFrame-gated by the only caller that matters, LandingPage.tsx)
// rather than adding a second new prop. Below 1024px, and for every
// `compact=false` caller (today's production homepage, /moms), this cap
// and the Track padding below render byte-identically.
//
// `$wideGutter` is a SECOND, independent gate (giftGuideHubFlag.ts, Kate
// 2026-10-27) layered UNDER `$compact`, not substituted for it: `$compact`
// alone already covers THE-1081's typography (left-aligned heading, flat
// quote size, regular attribution weight), which is unrelated to this
// ticket and must keep rendering for anyone who already has `hubFrame` on.
// `$wideGutter` only decides whether the GUTTER geometry below additionally
// un-caps — both must be true for the new geometry to show, and `$compact`
// alone (today's shipped behaviour under hubFrame) still renders the pre-PR
// 1280px-cap/flat-64px box the uncap/PAGE_GUTTER_DESKTOP lines replaced.
const OuterWrap = styled.section<{ $compact?: boolean; $wideGutter?: boolean }>`
  max-width: 1280px;
  margin: 0 auto;
  padding: 64px 0 0;
  @media (min-width: 768px) {
    padding: 88px 0 0;
  }
  @media (min-width: 1024px) {
    ${({ $compact, $wideGutter }) =>
      $compact && $wideGutter && 'max-width: none; margin: 0;'}
  }
`;

const Heading = styled.h2<{ $compact?: boolean; $wideGutter?: boolean }>`
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.025em;
  color: hsl(var(--foreground));
  text-align: center;
  margin: 0 0 32px 0;
  line-height: 1.1;
  padding: 0 16px;
  @media (min-width: 768px) {
    font-size: 40px;
    margin: 0 0 48px 0;
  }

  /* THE-1081: left-aligned section-title register (26/28px) matching the
     gift-guides hub's shared heading (hubType.ts HUB_HEADING_CSS) instead of
     the marketing display size, and inset to match Track's gutter below so
     the heading lines up with the first card. Opt-in so every other caller
     (today's production homepage, /moms) renders byte-identically.

     THE-1161 shepherd QA round 8: the >=1024px padding here used to stay a
     flat 64px while Track's own >=1024px padding (below) moved onto
     PAGE_GUTTER_DESKTOP, which only equals 64px up to 1568px wide and grows
     past it — so the two drifted apart above that width (measured live:
     heading at x=64, first card at x=144/240 at 1728/1920) even though both
     are declared $compact-only and look like they move together. Reading
     the SAME gutter here is what keeps the heading's left edge matching the
     first card's at every width, not just below 1568px. $wideGutter
     (giftGuideHubFlag.ts, Kate 2026-10-27) gates ONLY that >=1024px
     padding value, not the rest of this $compact-only block — the
     typography above it is THE-1081's, unrelated to this ticket, and must
     keep rendering for anyone who already has hubFrame on regardless of
     the new flag. NOTE: no backticks in this comment — it sits inside a
     styled-components tagged template literal. */
  ${({ $compact }) =>
    $compact &&
    css`
      font-size: 26px;
      text-align: left;
      padding: 0 16px;
      @media (min-width: 640px) {
        padding: 0 32px;
      }
      @media (min-width: 768px) {
        font-size: 28px;
      }
      @media (min-width: 1024px) {
        padding: 0
          ${({ $wideGutter }: { $wideGutter?: boolean }) =>
            $wideGutter ? PAGE_GUTTER_DESKTOP : '64px'};
      }
    `}
`;

// Snap-mandatory horizontal scroller. Left-edge anchored on every
// breakpoint: the first testimonial sits flush at the left padding,
// subsequent cards fan out to the right and overflow off-screen. User
// scrolls right (or taps a dot) to bring the next card into view. Left
// padding matches BrowseMyFriendsSection / OccasionGrid section
// conventions (16 / 32 / 64) so the testimonials line up with the rest
// of the homepage. (Kate 2026-05-13: "should start at the far left and
// fan out to the right".) Right padding is small so the second card
// peeks in, signaling there's more.
const Track = styled.div<{ $compact?: boolean; $wideGutter?: boolean }>`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  /* Owns its horizontal axis -- THE-803 [PLAT]: every nested scroller
     declares overscroll-behavior on the axis it owns. The payoff is Android
     Chrome and laptop trackpads, where a flick running off the end chains
     into the document's horizontal axis and drives back/forward. NOT an iOS
     fix: measured on device 2026-10-05, this declaration makes no difference
     to iOS Safari's back gesture, which sits above the page out of CSS's
     reach. Latent today: THE-805 relaxed only the root's VERTICAL axis, so
     the root still carries overscroll-behavior-x: none as an outer belt and
     nothing chains here yet. This is the inner belt, gated so it cannot be
     dropped before it is the only one. Rationale and sweep:
     src/__tests__/railOverscrollContainment.test.ts. */
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scroll-padding-left: 16px;
  -webkit-overflow-scrolling: touch;
  padding: 8px 16px 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
  @media (min-width: 640px) {
    padding-left: 32px;
    padding-right: 32px;
    scroll-padding-left: 32px;
  }
  @media (min-width: 768px) {
    gap: 24px;
  }
  @media (min-width: 1024px) {
    padding-left: 64px;
    padding-right: 64px;
    scroll-padding-left: 64px;
    /* THE-1161: the same "two left edges on one page" bug as
       HomeGuideRail's own wideGutter (see that file's comment) — this
       track's old flat 64px never moved with compact, even though compact
       is already gated on the exact hubFrame condition that means the
       page's other sections have. Declared after the plain rule, in the
       same block, so it wins by source order; every compact=false caller
       renders the lines above byte-identically.
       $wideGutter (giftGuideHubFlag.ts, Kate 2026-10-27) is a SECOND,
       independent gate ANDed with $compact — see OuterWrap's comment for
       why neither substitutes for the other. No backticks in this
       comment, same reason as ComingUpStrip.tsx's Scroller comment: this
       sits inside a styled-components tagged template literal. */
    ${({ $compact, $wideGutter }) =>
      $compact &&
      $wideGutter &&
      'padding-left: ' +
        PAGE_GUTTER_DESKTOP +
        '; padding-right: ' +
        PAGE_GUTTER_DESKTOP +
        '; scroll-padding-left: ' +
        PAGE_GUTTER_DESKTOP +
        ';'}
  }
`;

const Card = styled.article`
  scroll-snap-align: start;
  flex: 0 0 76%;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  min-height: 220px;
  @media (min-width: 768px) {
    flex-basis: 56%;
    padding: 36px 32px;
    min-height: 240px;
  }
  @media (min-width: 1280px) {
    flex-basis: 44%;
  }
`;

const Quote = styled.p<{ $compact?: boolean }>`
  margin: 0;
  font-family: ${({ theme }) => theme.font.serif};
  font-size: 18px;
  line-height: 1.5;
  color: hsl(var(--foreground));
  font-style: italic;
  @media (min-width: 768px) {
    /* THE-1081: the italic serif is the one deliberate type exception, but
       its size still joins the 6-size system (18px, the card-title register)
       rather than bumping up on desktop. */
    font-size: ${({ $compact }) => ($compact ? '18px' : '20px')};
  }
`;

const Attribution = styled.p<{ $compact?: boolean }>`
  margin: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: ${({ $compact }) => ($compact ? 400 : 600)};
  letter-spacing: 0.02em;
  color: hsl(var(--muted-foreground));
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
`;

const Dot = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: ${({ $active }) => ($active ? '28px' : '8px')};
  height: 8px;
  border-radius: 9999px;
  background: ${({ $active, theme }) =>
    $active ? theme.color.clay : 'hsl(var(--border))'};
  transition: width 220ms ease, background 220ms ease;
  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.color.clay : 'hsl(var(--muted-foreground) / 0.5)'};
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px hsl(var(--ring) / 0.3);
  }
`;

export interface TestimonialsCarouselProps {
  heading?: string;
  items?: Testimonial[];
  /** Disable auto-advance (used by stories so Happo snapshots don't drift). */
  autoAdvance?: boolean;
  /**
   * THE-1081: the trimmed type register for the gift-guides hub preview —
   * left-aligned 26/28px heading, flat 18px quote (no desktop bump), and
   * regular-weight (not semibold) gray attribution. Opt-in so every other
   * caller (today's production homepage, /moms) renders byte-identically.
   */
  compact?: boolean;
  /**
   * THE-1161 (rides the Gift Guide hub gate; no preview switch of its own) — defaults to
   * `false` so every existing story/test and real visitor keeps rendering
   * today's production layout when the hub gate is off. ANDed with `compact`
   * internally (see OuterWrap's comment) rather than substituted for it.
   */
  wideGutterEnabled?: boolean;
}

export const TestimonialsCarousel: React.FC<TestimonialsCarouselProps> = ({
  heading = 'What people are saying',
  items = DEFAULT_TESTIMONIALS,
  autoAdvance = true,
  compact,
  wideGutterEnabled = false,
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // Programmatic scrolls fire IntersectionObserver entries that look identical
  // to user scrolls. Track when WE initiated a scroll so the "scroll happened"
  // signal doesn't reset the dwell timer mid-flight.
  const programmaticScrollUntilRef = useRef(0);

  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    const el = cardRefs.current[index];
    if (!track || !el) return;
    programmaticScrollUntilRef.current = Date.now() + 800;
    // Scroll so the card's left edge lines up with the track's
    // scroll-padding-left (cards snap left, not centered, per the
    // 2026-05-13 "fan out from the left" layout call). Subtract the
    // computed scroll-padding-left so the snap point matches the
    // CSS `scroll-padding-left: 16/32/64px` declared on Track.
    // scrollIntoView would also yank the page vertically — we want to
    // scroll the TRACK only.
    const padLeft = parseInt(
      window.getComputedStyle(track).scrollPaddingLeft || '0',
      10,
    ) || 0;
    track.scrollTo({ left: el.offsetLeft - padLeft, behavior: 'smooth' });
  }, []);

  // Track which card is most-centered via IntersectionObserver. The entry
  // with the highest intersection ratio wins.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length === 0) return;
        const winner = visible[0];
        const idx = cardRefs.current.findIndex((el) => el === winner.target);
        if (idx >= 0) setActiveIndex(idx);
      },
      {
        root: track,
        threshold: [0.4, 0.6, 0.8, 1],
      },
    );
    cardRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items.length]);

  // Auto-advance: 5s after the active card stabilizes, scroll to the next
  // one. Pauses when the user is hovering so they can read in peace.
  useEffect(() => {
    if (!autoAdvance) return;
    if (paused) return;
    if (items.length <= 1) return;
    const t = window.setTimeout(() => {
      const next = (activeIndex + 1) % items.length;
      scrollToIndex(next);
    }, DWELL_MS);
    return () => window.clearTimeout(t);
  }, [activeIndex, items.length, paused, scrollToIndex, autoAdvance]);

  return (
    <OuterWrap aria-label="Testimonials" $compact={compact} $wideGutter={wideGutterEnabled}>
      <Heading $compact={compact} $wideGutter={wideGutterEnabled}>{heading}</Heading>
      <Track
        $compact={compact}
        $wideGutter={wideGutterEnabled}
        ref={trackRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        {items.map((t, i) => (
          <Card
            key={i}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            aria-roledescription="testimonial"
            aria-label={`Testimonial ${i + 1} of ${items.length}, by ${t.attribution}`}
          >
            <Quote $compact={compact}>“{t.quote}”</Quote>
            <Attribution $compact={compact}>— {t.attribution}</Attribution>
          </Card>
        ))}
      </Track>
      <Dots role="tablist" aria-label="Testimonial selection">
        {items.map((_, i) => (
          <Dot
            key={i}
            $active={i === activeIndex}
            type="button"
            role="tab"
            aria-selected={i === activeIndex}
            aria-label={`Go to testimonial ${i + 1}`}
            onClick={() => scrollToIndex(i)}
          />
        ))}
      </Dots>
    </OuterWrap>
  );
};
